/**
 * React Query hooks + types for the Stores tool (Store Operating System /
 * Store Cockpit, Phase 1: the communication wedge).
 *
 * Every endpoint lives under /v1/stores/* on the shared API. Binary
 * access-grant model (spec 00_access-binary-grants.md, 2026-08-09): the tool
 * serves several audiences, each on its OWN accessSlug, no levels:
 *   - stores.sweetspot -> the store-facing app (daily routine, thread, Inventory Count)
 *   - stores.scorecard / .questions / .broadcast / .photos / .variance / .pulse -> the 6 HQ tools, independently grantable
 *   - stores.users / .tablets -> store + tablet administration, independently
 *     grantable. stores.users also gates the checklist-definition admin page
 *     (Store checklist), because the api route reuses that capability.
 *
 * Data migration expanded every pre-existing stores@viewer/editor/admin grant
 * into the matching new rows (see the spec's migration table), so no one lost
 * access on cutover; going forward each of the slugs above is granted or
 * revoked on its own.
 *
 * `hasStoresStore` and `hasStoresHq` below are coarse "does this account hold
 * ANY tool in this family" checks, used only for UI-mode toggles (which store
 * picker to show, whether Ask Amp offers a store-scope select) — NOT as
 * page-level access gates. Each HQ page's own canView checks its own
 * accessSlug directly via `hasAccessSlug` from toolVisibility.ts, so revoking
 * one HQ tool (e.g. stores.broadcast) actually hides only that page.
 *
 * Graceful degradation: the backend is live on dev, but any 404 (route not
 * deployed) / 503 (feature not configured) is treated as a quiet "not available
 * yet" state by the pages, never a crash. An unmapped store user gets a 403 with
 * an 'unmapped' reason, surfaced as a dedicated "contact HQ" state.
 *
 * Mirrors the react-query patterns in useConnect.ts / useCoas.ts.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import { apiGet, apiPost, apiPut, apiPatch, apiDelete, type ApiError } from './api';
import { API_BASE } from './apiBase';

export const STORES_API_BASE = `${API_BASE}/v1/stores`;
// The store<->HQ thread, questions, and broadcasts are consolidated onto the
// chat platform: a store's conversation is its chat channel (store:{no}), a
// question is a chat_message with kind='question', HQ answers are kind='answer',
// broadcasts are kind='broadcast'. These hooks read/write the chat endpoints.
export const CHAT_API_BASE = `${API_BASE}/v1/chat`;

// --------------------------------------------------------------------------
// RBAC
// --------------------------------------------------------------------------

// The 6 independently-grantable Stores HQ tools. Used only for the coarse
// "is this an HQ account" signal (isHq UI-mode toggles below), never as a
// page-level access gate.
const HQ_TOOL_SLUGS = [
  'stores.scorecard',
  'stores.questions',
  'stores.broadcast',
  'stores.photos',
  'stores.variance',
  'stores.pulse',
];
const ADMIN_TOOL_SLUGS = ['stores.users', 'stores.tablets'];

// The store-facing app (Today, Store thread, Inventory Count): one grant,
// stores.sweetspot, covers all of it.
export function hasStoresStore(perms: string[] | undefined, superAdmin = false): boolean {
  if (superAdmin) return true;
  return !!perms?.includes('stores.sweetspot');
}

// Coarse "holds at least one HQ tool" signal — see the module doc-comment.
export function hasStoresHq(perms: string[] | undefined, superAdmin = false): boolean {
  if (superAdmin) return true;
  if (!perms) return false;
  return HQ_TOOL_SLUGS.some((s) => perms.includes(s));
}

// True when the signed-in user is a store-only account: they hold
// stores.sweetspot and none of the HQ or admin tools, and nothing outside the
// stores.* / core:* families (core:read is the session baseline every Cockpit
// account needs; core:write covers pins). Store Google accounts get only
// explicit email-keyed grants, so this cleanly separates store staff from HQ
// employees. Drives the dedicated mobile store shell: no department sidebar,
// store tools only. Not given a super-admin bypass: a super-admin's
// permissions array will not contain stores.sweetspot unless explicitly
// granted, so the first check below already excludes them.
export function isStoreOnlyUser(perms: string[] | undefined): boolean {
  if (!perms || perms.length === 0) return false;
  if (!perms.includes('stores.sweetspot')) return false;
  if ([...HQ_TOOL_SLUGS, ...ADMIN_TOOL_SLUGS].some((s) => perms.includes(s))) return false;
  return perms.every((p) => p === 'stores.sweetspot' || p.startsWith('core:'));
}

// A 404 (route not yet deployed) or 503 (feature not configured) means this
// backend slice is not live. Pages render a quiet placeholder rather than error.
export function isNotAvailable(error: unknown): boolean {
  const status = (error as ApiError | null)?.status;
  return status === 404 || status === 503;
}

// The scope resolver returns 403 with reason 'unmapped' when the caller has no
// store assignment (a store user not yet linked, or an HQ user who has not
// picked a store). That is a distinct state from a plain capability denial:
// the account is valid but has no store scope. The API nests extras inside the
// error envelope ({ error: { code, message, reason } }); the top-level read is
// kept for tolerance of older shapes.
export function isUnmapped(error: unknown): boolean {
  const e = error as ApiError | null;
  if (!e || e.status !== 403) return false;
  const body = e.body as { reason?: string; error?: { reason?: string } } | null;
  return (body?.error?.reason ?? body?.reason) === 'unmapped';
}

export function isForbidden(error: unknown): boolean {
  return (error as ApiError | null)?.status === 403;
}

// Generate Import File refuses (409) when a Generated file is already sitting
// unimported, so a second click cannot silently produce two files covering
// overlapping counts. The page reads this to offer "cancel the old file and
// roll its counts into this one" instead of just failing.
export function isOpenImportExists(error: unknown): boolean {
  const e = error as ApiError | null;
  if (!e || e.status !== 409) return false;
  // The api throws ApiError('conflict', ..., { reason: 'open_import_exists' }),
  // which serializes as { error: { code: 'conflict', reason, existing_import } }.
  const body = e.body as { reason?: string; error?: { reason?: string } } | null;
  return (body?.error?.reason ?? body?.reason) === 'open_import_exists';
}

// Generate Import File also refuses (422 validation_error) when there is
// nothing submitted and unimported to put in a file at all.
export function isNothingToImport(error: unknown): boolean {
  const e = error as ApiError | null;
  if (!e || e.status !== 422) return false;
  const body = e.body as { reason?: string; error?: { reason?: string } } | null;
  return (body?.error?.reason ?? body?.reason) === 'nothing_to_import';
}

// --------------------------------------------------------------------------
// Shared types
// --------------------------------------------------------------------------

export type StorePhase = 'open' | 'midday' | 'close';
export const STORE_PHASES: StorePhase[] = ['open', 'midday', 'close'];
export const PHASE_LABELS: Record<StorePhase, string> = {
  open: 'Opening',
  midday: 'Midday',
  close: 'Closing',
};

export type StoreRole = 'manager' | 'staff' | 'hq';

export type ChecklistItemRef = { id: number; label: string };

export type StoreMe = {
  store: {
    store_no: number;
    store_name: string;
    region: string | null;
    district: string | null;
  } | null;
  role: StoreRole;
  checklist: {
    open: ChecklistItemRef[];
    midday: ChecklistItemRef[];
    close: ChecklistItemRef[];
  };
};

export type ReportPhoto = {
  id: number;
  s3_key: string;
  caption: string | null;
  created_at: string;
};

export type ReportChecklistState = { item_id: number; checked: boolean };

export type StoreReport = {
  id: number;
  store_no: number;
  report_date: string;
  phase: StorePhase;
  traffic_count: number | null;
  traffic_source: 'auto' | 'manual' | null;
  transactions: number | null;
  note: string | null;
  submitted_by: string | null;
  submitted_at: string | null;
  photos: ReportPhoto[];
  checklist: ReportChecklistState[];
};

export type PhaseState = { submitted: boolean; report?: StoreReport };

export type ReportsResponse = {
  date: string;
  store_no: number;
  phases: {
    open: PhaseState;
    midday: PhaseState;
    close: PhaseState;
  };
  traffic_auto: { traffic: number | null; transactions: number | null } | null;
};

export type ThreadKind = 'message' | 'question' | 'answer' | 'broadcast' | 'system';
export type ThreadUrgency = 'low' | 'normal' | 'high' | 'urgent';
export type ThreadStatus = 'open' | 'answered' | 'closed';

// Raw chat message shape (subset) returned by /v1/chat endpoints. Retained for
// the HQ question queue's answer mutation; the store<->HQ thread views moved to
// the shared ChatWidget (shell/ChatBubbleMount).
type ChatRawMessage = {
  id: number;
  author_email: string;
  author_name: string | null;
  body: string;
  created_at: string | null;
  kind?: string;
  question_category?: string | null;
  urgency?: string | null;
  status?: string | null;
};

// --------------------------------------------------------------------------
// Store-scoped: identity + checklist
// --------------------------------------------------------------------------

// `storeNo` is the HQ path: an HQ user (stores:hq) may act on any store by
// passing ?store_no=. Store users omit it and act on their mapped store.
export function useStoreMe(storeNo?: number): UseQueryResult<StoreMe, Error> {
  return useQuery<StoreMe, Error>({
    queryKey: ['stores', 'me', storeNo ?? 'own'],
    queryFn: () => {
      const qs = storeNo !== undefined ? `?store_no=${storeNo}` : '';
      return apiGet<StoreMe>(`${STORES_API_BASE}/me${qs}`);
    },
    staleTime: 60_000,
    retry: false,
  });
}

// --------------------------------------------------------------------------
// Store-scoped: daily reports
// --------------------------------------------------------------------------

export function useStoreReports(
  date?: string,
  storeNo?: number,
): UseQueryResult<ReportsResponse, Error> {
  return useQuery<ReportsResponse, Error>({
    queryKey: ['stores', 'reports', date ?? 'today', storeNo ?? 'own'],
    queryFn: () => {
      const params = new URLSearchParams();
      if (date) params.set('date', date);
      if (storeNo !== undefined) params.set('store_no', String(storeNo));
      const qs = params.size > 0 ? `?${params.toString()}` : '';
      return apiGet<ReportsResponse>(`${STORES_API_BASE}/reports${qs}`);
    },
    staleTime: 15_000,
    retry: false,
  });
}

export type SubmitReportInput = {
  date: string;
  phase: StorePhase;
  traffic_count?: number | null;
  traffic_source?: 'auto' | 'manual';
  transactions?: number | null;
  note?: string;
  checklist?: ReportChecklistState[];
  store_no?: number;
};

export function useSubmitReport() {
  const qc = useQueryClient();
  return useMutation<{ report: StoreReport }, Error, SubmitReportInput>({
    mutationFn: (input) => apiPost<{ report: StoreReport }>(`${STORES_API_BASE}/reports`, input),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['stores', 'reports', vars.date] });
      void qc.invalidateQueries({ queryKey: ['stores', 'reports', 'today'] });
    },
  });
}

export type PhotoRequest = { content_type?: string; caption?: string };
export type PhotoResponse = { photo_id: number; key: string; upload_url: string };

export function useRequestPhotoUpload() {
  return useMutation<PhotoResponse, Error, { reportId: number; body: PhotoRequest }>({
    mutationFn: ({ reportId, body }) =>
      apiPost<PhotoResponse>(`${STORES_API_BASE}/reports/${reportId}/photos`, body),
  });
}

// Presigned GET for a stored photo (15-min). Cross-store reads 404 server-side.
export function useStorePhotoUrl(photoId: number | undefined) {
  return useQuery<{ url: string }, Error>({
    queryKey: ['stores', 'photo', photoId ?? 0],
    queryFn: () => apiGet<{ url: string }>(`${STORES_API_BASE}/photos/${photoId}`),
    enabled: photoId != null,
    staleTime: 10 * 60_000,
    retry: false,
  });
}

// --------------------------------------------------------------------------
// HQ: scorecard
// --------------------------------------------------------------------------

export type ScorecardRow = {
  store_no: number;
  store_name: string;
  region: string | null;
  pilot16: boolean;
  open_submitted: boolean;
  midday_submitted: boolean;
  close_submitted: boolean;
  photo_count: number;
  traffic: number | null;
  transactions: number | null;
  open_questions: number;
};

export type ScorecardResponse = {
  date: string;
  stores: ScorecardRow[];
  totals: { reported: number; missing: number };
};

export function useScorecard(date?: string): UseQueryResult<ScorecardResponse, Error> {
  return useQuery<ScorecardResponse, Error>({
    queryKey: ['stores', 'scorecard', date ?? 'today'],
    queryFn: () => {
      const qs = date ? `?date=${encodeURIComponent(date)}` : '';
      return apiGet<ScorecardResponse>(`${STORES_API_BASE}/hq/scorecard${qs}`);
    },
    staleTime: 15_000,
    retry: false,
  });
}

// The XLSX export is a direct browser navigation (returns a raw attachment).
export function scorecardExportUrl(date?: string): string {
  const qs = date ? `?date=${encodeURIComponent(date)}` : '';
  return `${STORES_API_BASE}/hq/scorecard/export${qs}`;
}

// Store options for the ChatWidget's Ask Amp store select ({ value, label },
// sorted by store number). Sources the HQ scorecard endpoint, the same list HqBroadcast's
// custom store picker uses, so no new backend route is needed. The chat bubble
// mounts on every page, so this fetches once and stays fresh for the session
// (long staleTime); it is enabled only for stores:hq holders.
export type AskStoreOption = { value: string; label: string };

// How a store is named everywhere it is picked: number, then name (for example
// "12 - Cherry Creek"). Falls back to whichever half exists.
export function storeLabel(storeNo: number | string | null | undefined, storeName: string | null | undefined): string {
  const no = storeNo === null || storeNo === undefined ? '' : String(storeNo);
  return [no, storeName?.trim() ?? ''].filter(Boolean).join(' - ');
}

export function useAskStores(enabled: boolean): UseQueryResult<AskStoreOption[], Error> {
  return useQuery<AskStoreOption[], Error>({
    queryKey: ['stores', 'ask-stores'],
    queryFn: async () => {
      const r = await apiGet<ScorecardResponse>(`${STORES_API_BASE}/hq/scorecard`);
      return (r.stores ?? [])
        .sort((a, b) => a.store_no - b.store_no)
        .map((s) => ({ value: String(s.store_no), label: storeLabel(s.store_no, s.store_name) }));
    },
    enabled,
    staleTime: 30 * 60_000,
    retry: false,
  });
}

// --------------------------------------------------------------------------
// HQ: question queue
// --------------------------------------------------------------------------

export type HqQuestion = {
  id: number;
  store_no: string;
  store_name: string;
  author_name: string | null;
  body: string;
  question_category: string | null;
  urgency: ThreadUrgency;
  status: ThreadStatus;
  created_at: string;
};

export type QuestionFilters = { status?: string; urgency?: string };

export function useHqQuestions(filters: QuestionFilters) {
  const query = useQuery<{ questions: HqQuestion[] }, Error>({
    queryKey: ['stores', 'questions', filters],
    queryFn: () => {
      const qs = new URLSearchParams();
      if (filters.status) qs.set('status', filters.status);
      if (filters.urgency) qs.set('urgency', filters.urgency);
      const suffix = qs.toString() ? `?${qs.toString()}` : '';
      return apiGet<{ questions: HqQuestion[] }>(`${CHAT_API_BASE}/questions${suffix}`);
    },
    staleTime: 10_000,
    retry: false,
  });
  return {
    questions: query.data?.questions ?? [],
    isLoading: query.isLoading,
    error: query.error,
    query,
  };
}

export function useAnswerQuestion() {
  const qc = useQueryClient();
  return useMutation<{ message: ChatRawMessage }, Error, { id: number; body: string }>({
    mutationFn: ({ id, body }) =>
      apiPost(`${CHAT_API_BASE}/questions/${id}/answer`, { body }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['stores', 'questions'] });
      void qc.invalidateQueries({ queryKey: ['stores', 'hq-thread'] });
      void qc.invalidateQueries({ queryKey: ['stores', 'scorecard'] });
      void vars;
    },
  });
}

// Closes a question without an official answer (resolved out-of-band, or
// stale). POST /v1/chat/questions/{id}/close returns { ok: true }; a 409 means
// the question was already answered or closed by someone else.
export function useCloseQuestion() {
  const qc = useQueryClient();
  return useMutation<{ ok: true }, Error, { id: number }>({
    mutationFn: ({ id }) => apiPost(`${CHAT_API_BASE}/questions/${id}/close`, {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['stores', 'questions'] });
      void qc.invalidateQueries({ queryKey: ['stores', 'hq-thread'] });
      void qc.invalidateQueries({ queryKey: ['stores', 'scorecard'] });
    },
  });
}

// --------------------------------------------------------------------------
// HQ: sidebar alert-bubble counts (api PR #548, 2026-08-09). One endpoint
// backs every bubble in the sidebar (Questions, Store photos, Merch Pulse) so
// the shell fetches once and every link reads off the same result, rather
// than each link polling its own count. No dedicated accessSlug: the server
// gates on holding any HQ Stores tool, so this hook mirrors that with the
// coarse hasStoresHq() check rather than a specific capability. A 403 (no HQ
// access), a 404/503 (route not deployed yet), or any other failure all
// degrade the same way: no bubbles, and the sidebar never breaks over it.
// --------------------------------------------------------------------------

export type HqBadges = {
  questions_open: number;
  pulse_open: number;
  photos_today: number;
};

export function useHqBadges(
  perms: string[] | undefined,
  superAdmin = false,
): HqBadges | null {
  const enabled = hasStoresHq(perms, superAdmin);
  const query = useQuery<HqBadges, Error>({
    queryKey: ['stores', 'hq-badges'],
    queryFn: () => apiGet<HqBadges>(`${STORES_API_BASE}/hq/badges`),
    enabled,
    staleTime: 60_000,
    refetchInterval: 60_000,
    retry: false,
  });
  return query.data ?? null;
}

// --------------------------------------------------------------------------
// HQ: merch pulse queue. Store staff flag a merchandising signal against a
// SKU (too much on the shelf, send more, a customer requested it, a problem,
// or something else), with an optional note. This is the only place in
// Cockpit that surfaces those signals; putting them on the Allocation grid
// was rejected (that grid was trimmed for browser performance, and a signal
// per store per SKU there would slow it back down). Modeled on the question
// queue above: newest-first, filterable, with an Acknowledge action that
// drops a signal off the open list.
// --------------------------------------------------------------------------

export type PulseSignal = 'too_much' | 'send_more' | 'requested' | 'problem' | 'other';

export const PULSE_SIGNALS: PulseSignal[] = ['too_much', 'send_more', 'requested', 'problem', 'other'];

export const PULSE_SIGNAL_LABELS: Record<PulseSignal, string> = {
  too_much: 'Too much',
  send_more: 'Send more',
  requested: 'Requested',
  problem: 'Problem',
  other: 'Other',
};

// Problem reads as the danger tone (matches URGENCY_TONES.urgent); the other
// four are distinct from each other but calm. All five reuse tones already in
// use elsewhere in Stores rather than inventing new colors.
export const PULSE_SIGNAL_TONES: Record<PulseSignal, string> = {
  too_much: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  send_more: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
  requested: 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300',
  problem: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  other: 'bg-gray-100 text-gray-600 dark:bg-gray-800/60 dark:text-gray-300',
};

export type PulseStatus = 'open' | 'acknowledged';

export const PULSE_STATUS_TONES: Record<PulseStatus, string> = {
  open: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  acknowledged: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
};

export type HqPulse = {
  id: number;
  store_no: string;
  store_name: string;
  item_no: string;
  descr: string | null;
  signal: PulseSignal;
  note: string | null;
  created_by_email: string;
  created_at: string;
  status: PulseStatus;
  acknowledged_by_email: string | null;
  acknowledged_at: string | null;
};

export type PulseFilters = {
  status?: string;
  signal?: string;
  store_no?: number;
  limit?: number;
};

function pulseQs(f: PulseFilters): string {
  const params = new URLSearchParams();
  if (f.status) params.set('status', f.status);
  if (f.signal) params.set('signal', f.signal);
  if (f.store_no !== undefined) params.set('store_no', String(f.store_no));
  if (f.limit !== undefined) params.set('limit', String(f.limit));
  const s = params.toString();
  return s ? `?${s}` : '';
}

export function useHqPulses(filters: PulseFilters) {
  const query = useQuery<{ pulses: HqPulse[] }, Error>({
    queryKey: ['stores', 'pulse', filters],
    queryFn: () => apiGet<{ pulses: HqPulse[] }>(`${STORES_API_BASE}/hq/pulse${pulseQs(filters)}`),
    staleTime: 10_000,
    retry: false,
  });
  return {
    pulses: query.data?.pulses ?? [],
    isLoading: query.isLoading,
    error: query.error,
    query,
  };
}

// Acknowledges one pulse. A 409 means someone else already acknowledged it
// between page load and this click: refetch so the row reflects the real
// state instead of surfacing an error banner for something that isn't
// actually wrong. A 404 (unknown id, e.g. a stale row) surfaces as a normal
// mutation error.
export function useAcknowledgePulse() {
  const qc = useQueryClient();
  return useMutation<{ ok: true }, Error, { id: number }>({
    mutationFn: ({ id }) => apiPost(`${STORES_API_BASE}/hq/pulse/${id}/acknowledge`, {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['stores', 'pulse'] });
      void qc.invalidateQueries({ queryKey: ['stores', 'hq-badges'] });
    },
    onError: (err) => {
      if ((err as ApiError).status === 409) {
        void qc.invalidateQueries({ queryKey: ['stores', 'pulse'] });
      }
    },
  });
}

// --------------------------------------------------------------------------
// HQ: broadcasts
// --------------------------------------------------------------------------

export type BroadcastAudience = 'all' | 'pilot' | 'custom';

export type Broadcast = {
  id: number;
  title: string;
  body: string;
  audience: BroadcastAudience;
  target_count: number;
  created_by: string | null;
  created_at?: string;
  /**
   * What the sender asked for, and what the send did. Stored on the broadcast by
   * api#481 follow-up (migration 0134), so history shows both instead of the
   * choices being request-only and the outcome living for five seconds.
   *
   * `null` means the broadcast was sent before that shipped, so the choice was
   * never recorded. That is NOT the same as the channel having been off, and the
   * UI must render it as unknown (indicators omitted) rather than as unchecked.
   */
  notify_boh_popup?: boolean | null;
  notify_store_email?: boolean | null;
  delivery_summary?: string | null;
};

export function useBroadcasts() {
  const query = useQuery<{ broadcasts: Broadcast[] }, Error>({
    queryKey: ['stores', 'broadcasts'],
    queryFn: () => apiGet<{ broadcasts: Broadcast[] }>(`${STORES_API_BASE}/hq/broadcasts`),
    staleTime: 15_000,
    retry: false,
  });
  return {
    broadcasts: query.data?.broadcasts ?? [],
    isLoading: query.isLoading,
    error: query.error,
    query,
  };
}

export type CreateBroadcastInput = {
  title: string;
  body: string;
  audience: BroadcastAudience;
  store_nos?: number[];
  /**
   * Whether to also pop the broadcast as a Windows message box on each store's
   * back-office PC, and whether to also email each store. The API defaults both
   * to true when they are absent, so the composer always sends them explicitly.
   */
  notify_boh_popup?: boolean;
  notify_store_email?: boolean;
};

/**
 * Per-channel delivery summary on the create response (api#481).
 *
 * Shape matches the API exactly: the popup tally lives under `channels.popup`
 * and carries one count per outcome, while the email tally is the top-level
 * `emails` object the API already returned. Everything is optional so a
 * response without them still types.
 *
 * `no_session` is the interesting one operationally: the store PC answered but
 * nobody was signed in, so nothing was displayed. The composer reads the
 * sentence the API composed from these counts (`broadcast.delivery_summary`)
 * rather than rebuilding it, so the composer and the history card agree; these
 * remain the per-channel detail behind that sentence.
 */
export type BroadcastPopupTally = {
  attempted?: number;
  sent?: number;
  no_session?: number;
  no_agent?: number;
  msg_missing?: number;
  error?: number;
};

export type BroadcastEmailTally = {
  sent?: number;
  skipped_no_address?: number;
  failed?: number;
  disabled?: true;
};

export type CreateBroadcastResponse = {
  broadcast: Broadcast;
  emails?: BroadcastEmailTally;
  channels?: {
    popup?: BroadcastPopupTally;
  };
};

export function useCreateBroadcast() {
  const qc = useQueryClient();
  return useMutation<CreateBroadcastResponse, Error, CreateBroadcastInput>({
    mutationFn: (input) => apiPost<CreateBroadcastResponse>(`${STORES_API_BASE}/hq/broadcasts`, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['stores', 'broadcasts'] });
    },
  });
}

// --------------------------------------------------------------------------
// --------------------------------------------------------------------------
// HQ: merchandising photos review
// --------------------------------------------------------------------------

export type HqPhoto = {
  photo_id: number;
  store_no: number;
  store_name: string;
  caption: string | null;
  created_at: string;
  url: string;
};

export type HqPhotosResponse = {
  date: string;
  photos: HqPhoto[];
};

export function useHqPhotos(date: string) {
  return useQuery<HqPhotosResponse, Error>({
    queryKey: ['stores', 'hq-photos', date],
    queryFn: () =>
      apiGet<HqPhotosResponse>(`${STORES_API_BASE}/hq/photos?date=${encodeURIComponent(date)}`),
    staleTime: 0,
    retry: false,
  });
}

// --------------------------------------------------------------------------
// Inventory count (shelf cycle counts + receiving checks). Store-scoped
// endpoints under /v1/stores/inventory/*, HQ variance report under
// /v1/stores/hq/inventory/variance. Items and on-hand come from the
// CounterPoint mirror tables (read-only); counts are captured in Cockpit.
// --------------------------------------------------------------------------

export type CountKind = 'shelf' | 'receiving';

/** One prior counted line for this item at this store, across open sessions. */
export type ExistingCount = {
  session_id: number;
  qty_counted: number;
  counted_by_email: string;
  updated_at: string;
};

export type InventoryItem = {
  item_no: string;
  descr: string | null;
  categ_cod: string | null;
  subcat_cod: string | null;
  /** Leadership's product taxonomy, mirrored from CounterPoint. */
  category: string | null;
  subcategory: string | null;
  /** CounterPoint's unit of measure (e.g. LB, EA), read-only. */
  stk_unit: string | null;
  stat: string | null;
  price: number | null;
  qty_on_hnd: number | null;
  barcode: string | null;
  /**
   * Prior live counts of this item at this store, across any open count
   * session (not just the one on screen), so two people counting at once
   * both see it. Absent or empty means nobody has counted the item yet in an
   * open session. Older api responses omit the field entirely.
   */
  existing_counts?: ExistingCount[];
  /** Sum of existing_counts' qty_counted. Convenience; also derivable client-side. */
  existing_total?: number;
};

export type CountSession = {
  id: number;
  store_no: string;
  kind: CountKind;
  po_no: string | null;
  status: 'open' | 'submitted';
  note: string | null;
  created_by_email: string;
  created_at: string | null;
  submitted_at: string | null;
  line_count: number;
};

export type CountLine = {
  id: number;
  item_no: string;
  descr: string | null;
  barcod: string | null;
  qty_counted: number;
  qty_on_hand_snap: number | null;
  qty_expected_snap: number | null;
  /** CounterPoint's unit of measure (e.g. LB, EA), read-only. Older api responses omit it. */
  stk_unit?: string | null;
  counted_by_email?: string;
  updated_at?: string | null;
};

export type PoSummary = {
  po_no: string;
  vend_no: string | null;
  vend_nam: string | null;
  ord_dat: string | null;
  deliv_dat: string | null;
  line_count: number;
  total_qty: number;
};

export type PoLine = {
  item_no: string | null;
  descr: string | null;
  expected_qty: number;
  /** CounterPoint's unit of measure (e.g. LB, EA), read-only. Older api responses omit it. */
  stk_unit?: string | null;
};

function storeNoQs(storeNo?: number, extra?: Record<string, string>): string {
  const params = new URLSearchParams(extra);
  if (storeNo !== undefined) params.set('store_no', String(storeNo));
  const s = params.toString();
  return s ? `?${s}` : '';
}

// Item lookup used by the scanner: pass a scanned barcode or a typed item no.
export function useItemLookup() {
  return useMutation<
    { found: boolean; item: InventoryItem | null },
    Error,
    { barcode?: string; item_no?: string; store_no?: number }
  >({
    mutationFn: ({ barcode, item_no, store_no }) => {
      const params = new URLSearchParams();
      if (barcode) params.set('barcode', barcode);
      if (item_no) params.set('item_no', item_no);
      if (store_no !== undefined) params.set('store_no', String(store_no));
      return apiGet(`${STORES_API_BASE}/inventory/item?${params.toString()}`);
    },
  });
}

export function useStorePos(storeNo?: number, enabled = true) {
  return useQuery<{ pos: PoSummary[] }, Error>({
    queryKey: ['stores', 'inventory', 'pos', storeNo ?? 'own'],
    queryFn: () => apiGet(`${STORES_API_BASE}/inventory/pos${storeNoQs(storeNo)}`),
    enabled,
    staleTime: 60_000,
    retry: false,
  });
}

export function useCountSessions(status?: 'open' | 'submitted', storeNo?: number) {
  return useQuery<{ sessions: CountSession[] }, Error>({
    queryKey: ['stores', 'inventory', 'sessions', status ?? 'all', storeNo ?? 'own'],
    queryFn: () =>
      apiGet(
        `${STORES_API_BASE}/inventory/sessions${storeNoQs(storeNo, status ? { status } : undefined)}`,
      ),
    staleTime: 10_000,
    retry: false,
  });
}

export type CountSessionDetail = {
  session: CountSession;
  lines: CountLine[];
  po_lines: PoLine[];
};

export function useCountSession(sessionId: number | undefined, storeNo?: number) {
  return useQuery<CountSessionDetail, Error>({
    queryKey: ['stores', 'inventory', 'session', sessionId ?? 0, storeNo ?? 'own'],
    queryFn: () =>
      apiGet(`${STORES_API_BASE}/inventory/sessions/${sessionId}${storeNoQs(storeNo)}`),
    enabled: sessionId != null,
    staleTime: 5_000,
    retry: false,
  });
}

export function useCreateCountSession() {
  const qc = useQueryClient();
  return useMutation<
    { session: CountSession },
    Error,
    { kind: CountKind; po_no?: string; note?: string; store_no?: number }
  >({
    mutationFn: (input) => apiPost(`${STORES_API_BASE}/inventory/sessions`, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['stores', 'inventory', 'sessions'] });
    },
  });
}

export function useUpsertCountLine(sessionId: number | undefined) {
  const qc = useQueryClient();
  return useMutation<
    { line: CountLine },
    Error,
    // 'add' sums onto any existing live count for the item; 'replace' flags the
    // earlier row and ignores it. 'set' is still accepted by the api as an
    // alias for 'replace' (back-compat), but new calls should use 'replace'.
    { item_no: string; barcod?: string; qty: number; mode?: 'add' | 'replace' | 'set'; store_no?: number }
  >({
    mutationFn: (input) =>
      apiPut(`${STORES_API_BASE}/inventory/sessions/${sessionId}/lines`, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['stores', 'inventory', 'session', sessionId ?? 0] });
    },
  });
}

// Deletes one counted line while its session is still open. 409 once the
// session is submitted (locked); the page surfaces that as a plain error.
export function useDeleteCountLine(sessionId: number | undefined) {
  const qc = useQueryClient();
  return useMutation<{ ok: true }, Error, { lineId: number; store_no?: number }>({
    mutationFn: ({ lineId, store_no }) =>
      apiDelete(`${STORES_API_BASE}/inventory/sessions/${sessionId}/lines/${lineId}${storeNoQs(store_no)}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['stores', 'inventory', 'session', sessionId ?? 0] });
    },
  });
}

// Discards an open session outright (used both for the explicit Discard
// button and for auto-discarding an open session with nothing scanned when
// the counter navigates back). 409 once submitted.
export function useDeleteCountSession() {
  const qc = useQueryClient();
  return useMutation<{ ok: true }, Error, { sessionId: number; store_no?: number }>({
    mutationFn: ({ sessionId, store_no }) =>
      apiDelete(`${STORES_API_BASE}/inventory/sessions/${sessionId}${storeNoQs(store_no)}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['stores', 'inventory', 'sessions'] });
    },
  });
}

export function useSubmitCountSession(sessionId: number | undefined) {
  const qc = useQueryClient();
  return useMutation<{ session: CountSession }, Error, { store_no?: number }>({
    mutationFn: (input) =>
      apiPost(`${STORES_API_BASE}/inventory/sessions/${sessionId}/submit`, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['stores', 'inventory'] });
    },
  });
}

// Per-store inventory variance report. One row per SKU that has CounterPoint
// on-hand > 0 (or has ever been counted), with counted / expected / variance
// against the CP snapshot plus who last counted the item. HQ passes ?store_no=N;
// store users get their mapped store implicitly.
export type VarianceRow = {
  item_no: string;
  descr: string | null;
  counted: number;
  expected: number | null;
  variance_qty: number | null;
  avg_cost: number | null;
  variance_cost: number | null;
  /** False when nobody has counted this item, which differs from counting zero. */
  is_counted: boolean;
  /**
   * Never counted. The counting-cycle wording ("due every N days") is gone;
   * the business decides what to count campaign by campaign, and this system
   * no longer tracks that. needs_count is simply the inverse of is_counted.
   */
  needs_count: boolean;
  last_counted_by_email: string | null;
  last_counted_at: string | null;
};

export type VarianceResponse = {
  store_no: number;
  rows: VarianceRow[];
  totals: { lines: number; with_variance: number; needs_count: number; variance_cost: number };
};

/**
 * Which slice of the report to load. `needs_count` is the pick list of what
 * still has to be counted; `variances` is only items someone counted whose
 * count disagrees with CounterPoint, so an item counted at zero stays visible
 * and an untouched item does not read as a shortage.
 */
export type VarianceView = 'all' | 'needs_count' | 'variances';

export type VarianceFilters = {
  store_no?: number;
  filter?: VarianceView;
};

function varianceQs(f: VarianceFilters): string {
  const params = new URLSearchParams();
  if (f.store_no !== undefined) params.set('store_no', String(f.store_no));
  params.set('filter', f.filter ?? 'all');
  const s = params.toString();
  return s ? `?${s}` : '';
}

export function useVariance(filters: VarianceFilters) {
  return useQuery<VarianceResponse, Error>({
    queryKey: ['stores', 'inventory', 'variance', filters],
    queryFn: () => apiGet(`${STORES_API_BASE}/inventory/variance${varianceQs(filters)}`),
    // Only fetch once a store is chosen (HQ picks a store before the grid loads).
    enabled: filters.store_no !== undefined,
    staleTime: 15_000,
    retry: false,
  });
}

export function varianceExportUrl(filters: VarianceFilters): string {
  return `${STORES_API_BASE}/inventory/variance/export${varianceQs(filters)}`;
}

// Client-side sort for the variance grid. The api endpoint returns the whole
// per-store dataset in a single response (one row per SKU with CP on-hand > 0
// or ever counted), so sorting stays in the browser — no cursor to keep in
// sync, no server sort param. Tri-state cycle (asc -> desc -> off) is owned by
// the page, encoded in ?sort=col:asc|desc so a refresh preserves the order.
// Mirrors the InvoicesList sort pattern.
export type VarianceSortColumn =
  | 'item_no'
  | 'descr'
  | 'counted'
  | 'expected'
  | 'variance_qty'
  | 'variance_cost'
  | 'last_counted_by_email'
  | 'last_counted_at';

export const VARIANCE_SORTABLE_COLUMNS: readonly VarianceSortColumn[] = [
  'item_no',
  'descr',
  'counted',
  'expected',
  'variance_qty',
  'variance_cost',
  'last_counted_by_email',
  'last_counted_at',
];

const VARIANCE_SORTABLE_SET: ReadonlySet<VarianceSortColumn> = new Set(VARIANCE_SORTABLE_COLUMNS);

// Sort key for a single row + column. Nullable numeric columns collapse to
// -Infinity so nulls sort to the bottom on desc and the top on asc (same
// convention InvoicesList uses for total_amount). Nullable string columns
// collapse to '' with the same effect.
function varianceSortValue(row: VarianceRow, col: VarianceSortColumn): string | number {
  switch (col) {
    case 'item_no': return row.item_no.toLowerCase();
    case 'descr': return (row.descr ?? '').toLowerCase();
    case 'counted': return row.counted;
    case 'expected': return row.expected ?? -Infinity;
    case 'variance_qty': return row.variance_qty ?? -Infinity;
    case 'variance_cost': return row.variance_cost ?? -Infinity;
    case 'last_counted_by_email': return (row.last_counted_by_email ?? '').toLowerCase();
    case 'last_counted_at': return row.last_counted_at ?? '';
  }
}

export function sortVarianceRows(
  rows: VarianceRow[],
  col: VarianceSortColumn,
  dir: 'asc' | 'desc',
): VarianceRow[] {
  const factor = dir === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const av = varianceSortValue(a, col);
    const bv = varianceSortValue(b, col);
    if (av < bv) return -1 * factor;
    if (av > bv) return 1 * factor;
    // Stable tie-break by item_no so equal-value rows have a deterministic order.
    if (a.item_no < b.item_no) return -1;
    if (a.item_no > b.item_no) return 1;
    return 0;
  });
}

export function parseVarianceSortParam(
  raw: string | null,
): { column: VarianceSortColumn; dir: 'asc' | 'desc' } | null {
  if (!raw) return null;
  const [col, dir] = raw.split(':');
  if (!col || !dir) return null;
  if (!VARIANCE_SORTABLE_SET.has(col as VarianceSortColumn)) return null;
  if (dir !== 'asc' && dir !== 'desc') return null;
  return { column: col as VarianceSortColumn, dir };
}

// --------------------------------------------------------------------------
// Inventory Imports (Cockpit only, admin only). The physical-count file sent
// to CounterPoint: every submitted shelf count not yet in a file, summed per
// store and item, ignoring replaced rows. Gated on stores.imports.manage.
// --------------------------------------------------------------------------

export type ImportFileStatus = 'generated' | 'imported' | 'cancelled';

export type ImportFile = {
  id: number;
  file_name: string;
  status: ImportFileStatus;
  generated_by_email: string;
  generated_at: string;
  imported_by_email: string | null;
  imported_at: string | null;
  cancelled_by_email: string | null;
  cancelled_at: string | null;
  note: string | null;
  session_count: number;
  line_count: number;
};

export const IMPORT_STATUS_TONES: Record<ImportFileStatus, string> = {
  generated: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  imported: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
  cancelled: 'bg-gray-100 text-gray-600 dark:bg-gray-800/60 dark:text-gray-300',
};

export function useImportFiles() {
  return useQuery<{ imports: ImportFile[] }, Error>({
    queryKey: ['stores', 'inventory', 'imports'],
    queryFn: () => apiGet(`${STORES_API_BASE}/inventory/imports`),
    staleTime: 10_000,
    retry: false,
  });
}

// Covers the whole fleet: every submitted shelf count not yet imported,
// whichever stores that turns out to be. Pass cancel_previous:true to cancel
// a sitting Generated file first and roll its counts into the new one.
export function useGenerateImportFile() {
  const qc = useQueryClient();
  return useMutation<{ import: ImportFile }, Error, { cancel_previous?: boolean } | undefined>({
    mutationFn: (input) => apiPost(`${STORES_API_BASE}/inventory/imports`, input ?? {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['stores', 'inventory', 'imports'] });
    },
  });
}

// Direct browser navigation; returns the exact bytes sent to CounterPoint.
export function importFileDownloadUrl(id: number): string {
  return `${STORES_API_BASE}/inventory/imports/${id}/file`;
}

export function useMarkImportImported() {
  const qc = useQueryClient();
  return useMutation<{ import: ImportFile }, Error, { id: number }>({
    mutationFn: ({ id }) => apiPost(`${STORES_API_BASE}/inventory/imports/${id}/imported`, {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['stores', 'inventory', 'imports'] });
    },
  });
}

// Clears the file's stamp off its sessions, so those counts fall into the
// next file on their own.
export function useCancelImportFile() {
  const qc = useQueryClient();
  return useMutation<{ import: ImportFile }, Error, { id: number }>({
    mutationFn: ({ id }) => apiPost(`${STORES_API_BASE}/inventory/imports/${id}/cancel`, {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['stores', 'inventory', 'imports'] });
    },
  });
}

export function useSetImportNote() {
  const qc = useQueryClient();
  return useMutation<{ import: ImportFile }, Error, { id: number; note: string }>({
    mutationFn: ({ id, note }) => apiPost(`${STORES_API_BASE}/inventory/imports/${id}/note`, { note }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['stores', 'inventory', 'imports'] });
    },
  });
}

// --------------------------------------------------------------------------
// Variance history: one row per counted line, reading the numbers frozen at
// count time (never recomputed against today's CounterPoint on-hand), so the
// report never changes after the fact. Gated stores:hq like the live
// variance report.
// --------------------------------------------------------------------------

export type HistoryRow = {
  session_id: number;
  store_no: string;
  item_no: string;
  descr: string | null;
  qty_counted: number;
  qty_on_hand_snap: number | null;
  variance_qty: number | null;
  avg_cost_snap: number | null;
  variance_cost: number | null;
  counted_by_email: string | null;
  counted_at: string | null;
  submitted_at: string | null;
};

export type HistoryStoreTotal = { store_no: string; lines: number; variance_qty: number; variance_cost: number };
export type HistoryMonthTotal = { month: string; lines: number; variance_qty: number; variance_cost: number };

export type HistoryResponse = {
  rows: HistoryRow[];
  totals: { by_store: HistoryStoreTotal[]; by_month: HistoryMonthTotal[] };
};

export type HistoryFilters = { store_no?: number; from?: string; to?: string };

function historyQs(f: HistoryFilters): string {
  const params = new URLSearchParams();
  if (f.store_no !== undefined) params.set('store_no', String(f.store_no));
  if (f.from) params.set('from', f.from);
  if (f.to) params.set('to', f.to);
  const s = params.toString();
  return s ? `?${s}` : '';
}

export function useVarianceHistory(filters: HistoryFilters) {
  return useQuery<HistoryResponse, Error>({
    queryKey: ['stores', 'inventory', 'history', filters],
    queryFn: () => apiGet(`${STORES_API_BASE}/inventory/history${historyQs(filters)}`),
    staleTime: 15_000,
    retry: false,
  });
}

// --------------------------------------------------------------------------
// Per-item notes + audit trail, keyed to (store_no, item_no). Backing api
// (api PR #320) returns both lists in a single GET so the modal renders
// notes + audit without a second round-trip.
//   GET  /v1/stores/inventory/items/{item_no}/notes?store_no=N
//        -> { notes: ItemNote[], audit: ItemAuditEvent[] }
//   POST /v1/stores/inventory/items/{item_no}/notes  { note, store_no? } -> { id }
// --------------------------------------------------------------------------

export type ItemNote = {
  id: number | string;
  store_no: number;
  item_no: string;
  note: string;
  created_by_email: string | null;
  created_at: string;
};

export type ItemAuditEvent = {
  id: number | string;
  ts: string;
  actor_email: string | null;
  action: string;
  outcome: string | null;
  detail?: unknown;
};

export type ItemNotesResponse = { notes: ItemNote[]; audit: ItemAuditEvent[] };

export function itemNotesKey(itemNo: string, storeNo: number | undefined) {
  return ['stores', 'inventory', 'item-notes', itemNo, storeNo ?? 'own'] as const;
}

export function useItemNotes(
  itemNo: string | null | undefined,
  storeNo: number | undefined,
  enabled = true,
) {
  return useQuery<ItemNotesResponse, Error>({
    queryKey: itemNotesKey(itemNo ?? '', storeNo),
    queryFn: () =>
      apiGet(
        `${STORES_API_BASE}/inventory/items/${encodeURIComponent(itemNo ?? '')}/notes${storeNoQs(storeNo)}`,
      ),
    enabled: !!itemNo && enabled,
    staleTime: 10_000,
    retry: false,
  });
}

export function useAddItemNote() {
  const qc = useQueryClient();
  return useMutation<
    { id: number | string },
    Error,
    { item_no: string; note: string; store_no?: number }
  >({
    mutationFn: ({ item_no, note, store_no }) =>
      apiPost(`${STORES_API_BASE}/inventory/items/${encodeURIComponent(item_no)}/notes`, {
        note,
        ...(store_no !== undefined ? { store_no } : {}),
      }),
    onSuccess: (_data, { item_no, store_no }) => {
      void qc.invalidateQueries({ queryKey: itemNotesKey(item_no, store_no) });
    },
  });
}

// --------------------------------------------------------------------------
// Admin: store-user mappings (email -> store + role). Requires stores:admin.
// Creating a mapping also grants the Stores Viewer access level server-side,
// so assigning a user to a store is one step.
// --------------------------------------------------------------------------

export type StoreUserRow = {
  id: number;
  email: string;
  store_no: string;
  role: 'manager' | 'staff';
  status: 'active' | 'disabled';
  created_at: string | null;
};

export function useStoreUsers(storeNo?: string) {
  return useQuery<{ users: StoreUserRow[] }, Error>({
    queryKey: ['stores', 'admin', 'users', storeNo ?? 'all'],
    queryFn: () =>
      apiGet(
        `${STORES_API_BASE}/admin/users${storeNo ? `?store_no=${encodeURIComponent(storeNo)}` : ''}`,
      ),
    staleTime: 10_000,
    retry: false,
  });
}

export function storeUsersExportUrl(storeNo?: string): string {
  return `${STORES_API_BASE}/admin/users/export${storeNo ? `?store_no=${encodeURIComponent(storeNo)}` : ''}`;
}

export function useCreateStoreUser() {
  const qc = useQueryClient();
  return useMutation<
    StoreUserRow & { grant_created: boolean },
    Error,
    { email: string; store_no: string; role?: 'manager' | 'staff' }
  >({
    mutationFn: (input) => apiPost(`${STORES_API_BASE}/admin/users`, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['stores', 'admin', 'users'] });
    },
  });
}

export function useUpdateStoreUser() {
  const qc = useQueryClient();
  return useMutation<
    StoreUserRow,
    Error,
    { id: number; store_no?: string; role?: 'manager' | 'staff'; status?: 'active' | 'disabled' }
  >({
    mutationFn: ({ id, ...body }) => apiPatch(`${STORES_API_BASE}/admin/users/${id}`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['stores', 'admin', 'users'] });
    },
  });
}

// --------------------------------------------------------------------------
// Store tablets (kiosk device register)
// --------------------------------------------------------------------------

/**
 * One store back-of-house tablet running The Sweet Spot as its main screen.
 * `status` is the whole security control: the API re-reads it on every request
 * the tablet makes, so revoking here lands on the device's next call.
 */
export type KioskDeviceRow = {
  id: number;
  store_no: string;
  store_name: string | null;
  label: string | null;
  serial: string | null;
  snipe_asset_tag: string | null;
  status: 'pending' | 'active' | 'revoked';
  /** The store's Google account the tablet acts as. */
  store_account: string | null;
  /** True while an unopened, unexpired enrollment link is outstanding. */
  enroll_pending: boolean;
  enroll_expires_at: string | null;
  enrolled_at: string | null;
  last_seen_at: string | null;
  last_seen_ip: string | null;
  last_seen_off_network: boolean | null;
  revoked_at: string | null;
  revoked_by: string | null;
  created_by: string;
  created_at: string | null;
};

export function useKioskDevices(filters?: { storeNo?: string; status?: string }) {
  const params = new URLSearchParams();
  if (filters?.storeNo) params.set('store_no', filters.storeNo);
  if (filters?.status) params.set('status', filters.status);
  const qs = params.toString();
  return useQuery<{ devices: KioskDeviceRow[] }, Error>({
    queryKey: ['stores', 'kiosk', 'devices', filters?.storeNo ?? 'all', filters?.status ?? 'all'],
    queryFn: () => apiGet(`${STORES_API_BASE}/kiosk/devices${qs ? `?${qs}` : ''}`),
    staleTime: 10_000,
    retry: false,
  });
}

/**
 * Register a tablet. The response's `enroll_url` is the ONLY place the token
 * ever appears, so the page shows it once and the operator carries it to the
 * tablet. Losing it means reissuing rather than retrieving.
 */
export function useCreateKioskDevice() {
  const qc = useQueryClient();
  return useMutation<
    { device: KioskDeviceRow; enroll_url: string },
    Error,
    { store_no: string; label?: string; serial?: string; snipe_asset_tag?: string }
  >({
    mutationFn: (input) => apiPost(`${STORES_API_BASE}/kiosk/devices`, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['stores', 'kiosk', 'devices'] });
    },
  });
}

/** A fresh link for a tablet that was factory reset in the field. */
export function useReissueKioskEnrollment() {
  const qc = useQueryClient();
  return useMutation<{ device: KioskDeviceRow; enroll_url: string }, Error, { id: number }>({
    mutationFn: ({ id }) => apiPost(`${STORES_API_BASE}/kiosk/devices/${id}/reissue`, {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['stores', 'kiosk', 'devices'] });
    },
  });
}

export type ManagedKioskBootstrapDevice = {
  device_id: number;
  store_no: string;
  label: string | null;
  serial: string;
  token: string;
  expires_at: string;
};

export type ManagedKioskBootstrapResult = {
  configuration_key: 'sweetspot_kiosk_token';
  devices: ManagedKioskBootstrapDevice[];
};

/** Short-lived companion tokens for the selected tablets' managed Android app. */
export function useManagedKioskBootstrap() {
  return useMutation<ManagedKioskBootstrapResult, Error, { device_ids: number[] }>({
    mutationFn: (input) => apiPost(`${STORES_API_BASE}/kiosk/managed-bootstrap`, input),
  });
}

export function useRevokeKioskDevice() {
  const qc = useQueryClient();
  return useMutation<{ device: KioskDeviceRow }, Error, { id: number }>({
    mutationFn: ({ id }) => apiPost(`${STORES_API_BASE}/kiosk/devices/${id}/revoke`, {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['stores', 'kiosk', 'devices'] });
    },
  });
}

// --------------------------------------------------------------------------
// Admin: the daily-routine checklist definition (open / midday / close items
// store managers check off in Today). Shares the stores.users accessSlug: the
// api route declares `capability: 'stores.users'` (tools/stores/src/routes.ts)
// rather than a dedicated slug, so the Store checklist page is gated the same
// as Store users and the two show as one row in the Access console. There is
// no delete endpoint; retiring an item is PATCH active=false, and reactivating
// it is PATCH active=true.
// --------------------------------------------------------------------------

export type ChecklistPhase = 'open' | 'midday' | 'close';

export type ChecklistItem = {
  id: number;
  phase: ChecklistPhase;
  label: string;
  sort: number;
  active: boolean;
};

export function useChecklistItems() {
  return useQuery<{ items: ChecklistItem[] }, Error>({
    queryKey: ['stores', 'admin', 'checklist'],
    queryFn: () => apiGet(`${STORES_API_BASE}/admin/checklist`),
    staleTime: 10_000,
    retry: false,
  });
}

export function useCreateChecklistItem() {
  const qc = useQueryClient();
  return useMutation<
    ChecklistItem,
    Error,
    { phase: ChecklistPhase; label: string; sort?: number; active?: boolean }
  >({
    mutationFn: (input) => apiPost(`${STORES_API_BASE}/admin/checklist`, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['stores', 'admin', 'checklist'] });
    },
  });
}

export function useUpdateChecklistItem() {
  const qc = useQueryClient();
  return useMutation<
    ChecklistItem,
    Error,
    { id: number; phase?: ChecklistPhase; label?: string; sort?: number; active?: boolean }
  >({
    mutationFn: ({ id, ...body }) => apiPatch(`${STORES_API_BASE}/admin/checklist/${id}`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['stores', 'admin', 'checklist'] });
    },
  });
}

// --------------------------------------------------------------------------
// Tones + labels
// --------------------------------------------------------------------------

export const URGENCY_ORDER: ThreadUrgency[] = ['urgent', 'high', 'normal', 'low'];

export function urgencyRank(u: ThreadUrgency): number {
  const i = URGENCY_ORDER.indexOf(u);
  return i === -1 ? URGENCY_ORDER.length : i;
}

export const URGENCY_TONES: Record<ThreadUrgency, string> = {
  urgent: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  high: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  normal: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
  low: 'bg-gray-100 text-gray-600 dark:bg-gray-800/60 dark:text-gray-300',
};

export function urgencyLabel(u: ThreadUrgency): string {
  return u.charAt(0).toUpperCase() + u.slice(1);
}

export const KIND_TONES: Record<ThreadKind, string> = {
  message: 'bg-gray-100 text-gray-600 dark:bg-gray-800/60 dark:text-gray-300',
  question: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
  answer: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
  broadcast: 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300',
  system: 'bg-gray-100 text-gray-500 dark:bg-gray-800/60 dark:text-gray-400',
};

export function kindLabel(k: ThreadKind): string {
  if (k === 'message') return 'Message';
  if (k === 'question') return 'Question';
  if (k === 'answer') return 'Answer';
  if (k === 'broadcast') return 'Broadcast';
  return 'System';
}

export const THREAD_STATUS_TONES: Record<ThreadStatus, string> = {
  open: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  answered: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
  closed: 'bg-gray-100 text-gray-600 dark:bg-gray-800/60 dark:text-gray-300',
};

// Question categories offered to store managers in the composer. Free-text on
// the backend; this is the starter list the UI presents.
export const QUESTION_CATEGORIES = [
  'Merchandising',
  'Inventory',
  'Staffing',
  'Facilities',
  'POS / Register',
  'Marketing',
  'Other',
] as const;

// Today's date in America/Denver (the store operating timezone), as YYYY-MM-DD.
// Matches the backend default so the "today" view and the submit payload agree.
export function denverToday(): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Denver',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
  return parts; // en-CA yields YYYY-MM-DD
}
